import { useEffect, useState } from 'react';
import api from '../api/client';
import {
  Alert,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Stack,
} from '@mui/material';
import videoStart from '../assets/images/play-video.avif';
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next'; // ✅ Import translation hook
import { useScreen } from '../hooks/useScreen';

export default function Cameras() {
  const { t } = useTranslation();
  const { isXs } = useScreen();
  const [cameras, setCameras] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [formData, setFormData] = useState({
    rtsp_url: '',
    location: '',
    nave: '',
    notes: '',
  });
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('user'));
  const isAdmin = Boolean(user?.is_admin);

  const fetchCameras = () => {
    api.get('/cameras/')
      .then(res => setCameras(res.data))
      .catch(err => console.error(err));
  };

  useEffect(() => {
    fetchCameras();

    // const ws = new WebSocketClient("/ws");

    // ws.onOpen(() => {
    //   console.log("✅ WebSocket connected");
    //   // Optional: ws.send("Hello server");
    // });

    // ws.onMessage((message) => {
    //   console.log("📨 WebSocket message:", message);

    //   // Optionally reload cameras if a camera is updated
    //   if (message === "refresh_cameras") {
    //     fetchCameras();
    //   }
    // });

    // ws.onClose(() => {
    //   console.log("❌ WebSocket disconnected");
    // });

    // return () => {
    //   ws.close();
    // };
  }, []);


  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCreateCamera = () => {
    const payload = {
      ...formData,
      client_id: user.client_id,
    };

    api.post('/cameras/', payload)
      .then(() => {
        setOpen(false);
        fetchCameras();
      })
      .catch(err => console.error(err));
  };

  const handleEditCamera = (camera, e) => {
    e?.currentTarget?.blur();
    setEditing(true);
    setFormData(camera);
    setOpen(true);
  };

  const handleUpdateCamera = () => {
    api.put(`/cameras/${formData.id}`, formData)
      .then(() => {
        setOpen(false);
        setEditing(false);
        fetchCameras();
      })
      .catch(err => console.error(err));
  };

  const handleDeleteCamera = (id) => {
    if (window.confirm(t('confirm_delete_camera'))) {
      api.delete(`/cameras/${id}`)
        .then(() => fetchCameras())
        .catch(err => console.error(err));
    }
  };

  const handleViewCamera = (cameraId) => {
    api.get(`/cameras/${cameraId}`)
      .then(res => setSelectedCamera(res.data))
      .catch(err => console.error(err));
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>{t('cameras')}</Typography>

      {isAdmin ? (
        <Button variant="contained" onClick={(e) => { 
          e.currentTarget.blur();
          setOpen(true); 
          setEditing(false); 
          setFormData({ rtsp_url: '', location: '', nave: '', notes: '' }); 
        }} sx={{ mb: 2 }}>
          {t('add_camera')}
        </Button>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          Solo un administrador puede agregar, editar o eliminar cámaras.
        </Alert>
      )}

      <Grid container spacing={3}>
        {cameras.map(camera => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={camera.id}>
            <Card sx={{ height: '100%' }}>
              <CardMedia
                component="img"
                height="180"
                image={videoStart}
                alt={t('camera_preview')}
              />
              <CardContent>
                <Typography variant="h6">{camera.location}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('nave')}: {camera.nave}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('notes')}: {camera.notes}
                </Typography>
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: '100%' }}>
                  <Button fullWidth={isXs} size="small"
                    onClick={() => navigate(`/dashboard/pig-counter/${camera.id}`)}>
                    {t('livestream')}
                  </Button>
                  <Button size="small" color="secondary" 
                    onClick={(e) => { e.currentTarget.blur(); handleViewCamera(camera.id); }}>
                    {t('settings')}
                  </Button>
                  {isAdmin && (
                    <>
                      <Button fullWidth={isXs} size="small" color="warning"
                        onClick={(e) => handleEditCamera(camera, e)}>
                        {t('edit')}
                      </Button>
                      <Button fullWidth={isXs} size="small" color="error"
                        onClick={() => handleDeleteCamera(camera.id)}>
                        {t('delete')}
                      </Button>
                    </>
                  )}
                </Stack>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* View Camera Modal */}
      {selectedCamera && (
        <Dialog open={Boolean(selectedCamera)} onClose={() => setSelectedCamera(null)} maxWidth="sm" fullWidth fullScreen={isXs}>  
          <DialogTitle>{t('camera_details')}</DialogTitle>
          <DialogContent>
            <Typography variant="body1"><strong>{t('location')}:</strong> {selectedCamera.location}</Typography>
            <Typography variant="body1"><strong>{t('nave')}:</strong> {selectedCamera.nave}</Typography>
            <Typography variant="body1"><strong>{t('notes')}:</strong> {selectedCamera.notes}</Typography>
            <Typography variant="body1"><strong>{t('rtsp_url')}:</strong> {selectedCamera.rtsp_url}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedCamera(null)}>{t('close')}</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Add / Edit Modal */}
      <Dialog open={open} onClose={() => setOpen(false)} fullScreen={isXs} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? t('edit_camera') : t('add_camera')}</DialogTitle>
        <DialogContent>
          {['rtsp_url', 'location', 'nave', 'notes'].map(field => (
            <TextField
              size={isXs ? 'small' : 'medium'}
              key={field}
              margin="dense"
              label={t(field)}  // ✅ Translate form labels
              name={field}
              value={formData[field] || ""}
              fullWidth
              onChange={handleChange}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button variant="contained" onClick={editing ? handleUpdateCamera : handleCreateCamera}>
            {editing ? t('update') : t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
